import { gql, useQuery } from "@apollo/client";
import { useModals } from "ModalProvider";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import ImageLoader from "components/base/Image";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { createContext, useState } from "react";
import { IoMdCheckmark, IoMdClose } from "react-icons/io";
import { searchByProp } from "utils/search";
import { byName } from "utils/sort";
import ProductLineForm from "./ProductLineForm";
import ProductLineLoader from "./ProductLineLoader";

export const FETCH_PRODUCT_LINES = gql`
  query FETCH_PRODUCT_LINES {
    allProductlines {
      id
      name
      number
      mainImage(size: "300x300")
      age
      activeForWis
      customsName
      customsBrand
      customsUsage
      customsValue
      customsSize
      customsMaterials
    }
  }
`;

export const AllProductsContext = createContext({});

const AllProductLines = () => {
  const { loading, error, data } = useQuery(FETCH_PRODUCT_LINES);
  const [searchText, setSearchText] = useState("");

  const newProductLineModal = useModals();
  const editProductLineModal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  function onCreatedProductLine(productLine) {
    editProductLineModal.present({
      title: "Edit Product Line",
      maxWidth: "max-w-full",
      children: <ProductLineLoader id={productLine.id} />,
    });
    newProductLineModal.hide();
  }

  const linesActiveForWis = data.allProductlines
    .filter((i) => i.activeForWis)
    .sort(byName);
  const inactiveLines = data.allProductlines
    .filter((i) => !i.activeForWis)
    .sort(byName);

  const lines = [...linesActiveForWis, ...inactiveLines].filter((i) =>
    searchByProp(i, ["name", "number"], searchText),
  );

  function edit(id) {
    editProductLineModal.present({
      title: "Edit Product Line",
      fullscreen: true,
      isBeingPresented: true,
      children: (
        <ProductLineLoader
          id={id}
          duplicate={(res) => {
            editProductLineModal.hide();
            newProductLineModal.present({
              title: "Create Product Line",
              fullscreen: true,
              children: (
                <ProductLineForm
                  duplicatingProduct={res}
                  onSave={onCreatedProductLine}
                />
              ),
            });
          }}
        />
      ),
    });
  }

  return (
    <div className="flex-1 overflow-auto flex flex-col card p-0 rounded-2xl relative m-6">
      <div className="flex sticky top-0 px-6 py-4 bg-white shadow-sm bg-opacity-80 backdrop-blur-lg dark:bg-gray-800 dark:bg-opacity-80 z-10 space-x-4">
        <div className="flex-1">
          <SearchBar
            autoFocus
            className="p-2 flex-1"
            value={searchText}
            onChange={setSearchText}
            placeholder="Search produc name, item number"
          />
        </div>

        <Button
          onClick={() =>
            newProductLineModal.present({
              title: "Create Product Line",
              fullscreen: true,
              children: <ProductLineForm onSave={onCreatedProductLine} />,
            })
          }
        >
          + Product Line
        </Button>
      </div>

      <div className="pt-0 relative">
        <table className="with-line">
          <tbody>
            {lines.map((i) => (
              <tr
                key={i.id}
                onClick={() => edit(i.id)}
                className=" hover:bg-blue-50 dark:hover:bg-blue-900 cursor-pointer"
              >
                <td>
                  <div className="flex items-center px-4">
                    <div className="w-8">
                      <ImageLoader size="w-8 h-8" src={i.mainImage} />
                    </div>
                    <div className="ml-4">
                      #{i.id}. {i.number ? `[ ${i.number} ] ` : null}
                      {i.name}
                    </div>
                  </div>
                </td>
                <td>
                  {i.customsName ? (
                    <div className="mr-6">
                      {i.customsName}, brand: {i.customsBrand}
                    </div>
                  ) : null}
                </td>
                <td>{i.age}</td>
                <td>
                  <div className="flex items-center justify-end space-x-2">
                    <div>WIS</div>
                    {i.activeForWis ? (
                      <IoMdCheckmark size={21} className="text-green-600" />
                    ) : (
                      <IoMdClose size={21} className="text-red-500" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllProductLines;
